import { ALL_LOCALES, DEFAULT_LOCALE, Locale } from '@color/lib';
import constate from 'constate';
import { useCallback, useState } from 'react';
import { matchPath } from 'react-router-dom';

import { ACCOUNT_SETTINGS_ROOT_PATH } from 'ui/Account/Settings/paths';
import {
  BASE_AUTO_SIGN_IN_PATH,
  BASE_CREATE_ACCOUNT_PATH,
  BASE_FORGOT_PASSWORD_PATH,
  BASE_SIGN_IN_PATH,
  BASE_SSO_PATH,
  BASE_UPDATE_PASSWORD_PATH,
} from 'ui/Auth/SignIn/paths';
import { CARE_DASHBOARD_PATH, CARE_DASHBOARD_STATUS_PATH, CARE_SIGN_UP_PATH } from 'ui/Care/paths';
import {
  CONSULTS_COVID_ANTIVIRALS_PATH,
  CONSULTS_ROOT_PATH,
  CONSULTS_SCHEDULED_CONSULT_V1_BASE_PATH,
} from 'ui/Consults/paths';
import {
  ACTIVATE_PATH,
  BASE_COVID_PROXY_PATH,
  BASE_COVID_RAPID_PROXY_PATH,
  BASE_COVID_SIGN_UP_PATH,
  COVID_ACTIVATE_PRODUCT_SELECTION_PATH,
  COVID_ACTIVATION_PATH,
  COVID_APPOINTMENTS_BASE_PATH,
  COVID_DASHBOARD_PATH,
  COVID_ELIGIBILITY_ROUTING,
  COVID_SELECT_PARTICIPANT_PATH,
  COVID_SELF_REPORTED_RESULTS_ROOT_PATH,
  COVID_SELF_REPORTING_PATH,
  COVID_USER_RESULTS_PATH_PREFIX,
} from 'ui/Covid/paths';
import { DOCUMENTS_ROOT_PATH } from 'ui/Documents/Upload/paths';
import { BASE_COVID_ORDER_FLOW_PATH } from 'ui/Purchase/Order/Covid/paths';
import { BASE_STI_ORDER_PATH, BASE_STI_PRE_ORDER_PATH } from 'ui/Purchase/paths';
import { BASE_COVID_PRE_ORDER_PATH } from 'ui/Purchase/PreOrder/Covid/paths';
import { BASE_DISCOVERY_PATH } from 'ui/routes/discovery/paths';
import { STI_ROOT_PATH } from 'ui/Sti/paths';
import { VACCINE_ROOT_PATH } from 'ui/Vaccine/paths';

const COMMON_LOCALES = [Locale.ENGLISH, Locale.SPANISH];

const SIGNED_IN_LOCALES = [
  ...COMMON_LOCALES,
  Locale.CHINESE_SIMPLIFIED,
  Locale.ARABIC,
  Locale.CHINESE_TRADITIONAL,
  Locale.POLISH,
  Locale.SAMOAN,
  Locale.FILIPINO,
  Locale.URDU,
  Locale.VIETNAMESE,
  Locale.HAITIAN_CREOLE,
  Locale.PORTUGUESE,
];

const COVID_TESTING_LOCALES = [
  ...COMMON_LOCALES,
  Locale.KOREAN,
  Locale.RUSSIAN,
  Locale.VIETNAMESE,
  Locale.ARABIC,
  Locale.FILIPINO,
  Locale.CHINESE_TRADITIONAL,
  Locale.SAMOAN,
  Locale.POLISH,
  Locale.UKRAINIAN,
  Locale.URDU,
];

export const VACCINE_LOCALES = [
  ...COMMON_LOCALES,
  Locale.CHINESE_SIMPLIFIED,
  Locale.PORTUGUESE,
  Locale.HAITIAN_CREOLE,
  Locale.VIETNAMESE,
  Locale.KHMER,
  Locale.ARABIC,
  Locale.CAPE_VERDEAN_CREOLE,
  Locale.FARSI,
  Locale.FILIPINO,
  Locale.FRENCH,
  Locale.SOMALI,
];

const CONSULT_COVID_ANTIVIRALS_FLOW_LOCALES = [
  Locale.ENGLISH,
  Locale.SPANISH,
  Locale.PORTUGUESE,
  Locale.HAITIAN_CREOLE,
  Locale.CHINESE_SIMPLIFIED,
  Locale.FILIPINO,
  Locale.RUSSIAN,
  Locale.SOMALI,
  Locale.FRENCH,
  Locale.KOREAN,
  Locale.VIETNAMESE,
  Locale.AMHARIC,
  Locale.MARSHALLESE,
  Locale.CHINESE_TRADITIONAL,
  Locale.ARABIC,
  Locale.HMONG,
  Locale.HINDI,
];

const CONSULT_TELEPREP_FLOW_LOCALES = [Locale.ENGLISH, Locale.SPANISH];

interface IPathLocaleOptions {
  locales: Locale[];
  exact: boolean;
  strict: boolean;
}

function generateExactPathLocaleOption(locales: Locale[]) {
  return {
    locales,
    exact: true,
    strict: true,
  };
}

function generatePathAndChildLocaleOption(locales: Locale[]) {
  return {
    locales,
    exact: false,
    strict: false,
  };
}

function getPathLocaleOptionsByBasePath(): Record<string, IPathLocaleOptions> {
  const PATH_LOCALE_OPTIONS_BY_BASE_PATH: Record<string, IPathLocaleOptions> = {
    [VACCINE_ROOT_PATH]: generatePathAndChildLocaleOption(VACCINE_LOCALES),
    [BASE_COVID_PROXY_PATH]: generatePathAndChildLocaleOption(COVID_TESTING_LOCALES),
    [BASE_COVID_SIGN_UP_PATH]: generatePathAndChildLocaleOption(COVID_TESTING_LOCALES),
    [COVID_APPOINTMENTS_BASE_PATH]: generatePathAndChildLocaleOption(COVID_TESTING_LOCALES),
    [BASE_DISCOVERY_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),
    [CONSULTS_COVID_ANTIVIRALS_PATH]: generatePathAndChildLocaleOption(
      CONSULT_COVID_ANTIVIRALS_FLOW_LOCALES
    ),
    [CONSULTS_SCHEDULED_CONSULT_V1_BASE_PATH]: generatePathAndChildLocaleOption(
      CONSULT_TELEPREP_FLOW_LOCALES
    ),
    [CONSULTS_ROOT_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),
    '/react/dev': generatePathAndChildLocaleOption(Object.values(Locale)),
    [BASE_SIGN_IN_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [BASE_AUTO_SIGN_IN_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [BASE_FORGOT_PASSWORD_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [BASE_CREATE_ACCOUNT_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [BASE_UPDATE_PASSWORD_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [BASE_SSO_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [BASE_COVID_RAPID_PROXY_PATH]: generatePathAndChildLocaleOption(COVID_TESTING_LOCALES),
    '/': generateExactPathLocaleOption(SIGNED_IN_LOCALES),
    [COVID_DASHBOARD_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [CARE_SIGN_UP_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [CARE_DASHBOARD_PATH]: generateExactPathLocaleOption(SIGNED_IN_LOCALES),
    [CARE_DASHBOARD_STATUS_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [COVID_ELIGIBILITY_ROUTING]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [COVID_SELECT_PARTICIPANT_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [COVID_USER_RESULTS_PATH_PREFIX]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),

    [ACTIVATE_PATH]: generateExactPathLocaleOption(SIGNED_IN_LOCALES),
    [COVID_ACTIVATE_PRODUCT_SELECTION_PATH]: generateExactPathLocaleOption(SIGNED_IN_LOCALES),
    [COVID_ACTIVATION_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),

    [COVID_SELF_REPORTING_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),
    [COVID_SELF_REPORTED_RESULTS_ROOT_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),

    [ACCOUNT_SETTINGS_ROOT_PATH]: generatePathAndChildLocaleOption(SIGNED_IN_LOCALES),

    [BASE_COVID_PRE_ORDER_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),
    [BASE_COVID_ORDER_FLOW_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),

    [STI_ROOT_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),
    [DOCUMENTS_ROOT_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),

    [BASE_STI_PRE_ORDER_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),
    [BASE_STI_ORDER_PATH]: generatePathAndChildLocaleOption(COMMON_LOCALES),
  };

  return PATH_LOCALE_OPTIONS_BY_BASE_PATH;
}

/**
 * Hook to configure and retrieve available locales.
 */
export function useLocaleConfig() {
  const [allowedLocales, setAllowedLocales] = useState<Locale[]>(ALL_LOCALES);

  /**
   * Returns the list of available locales based on the locales that have been translated for a given
   * flow (keyed by path) and allowedLocales overrides that restrict the set of locales that should
   * be available.
   */
  const getAvailableLocales = useCallback(
    (routePathname: string): Locale[] => {
      const PATH_LOCALE_OPTIONS_BY_BASE_PATH = getPathLocaleOptionsByBasePath();
      const translatedFlowPath = Object.keys(PATH_LOCALE_OPTIONS_BY_BASE_PATH).find(
        (pathToMatch) => {
          const { exact, strict } = PATH_LOCALE_OPTIONS_BY_BASE_PATH[pathToMatch];
          const matchedResults = matchPath(routePathname, {
            path: pathToMatch,
            exact,
            strict,
          });
          return matchedResults != null;
        }
      );

      if (!translatedFlowPath) {
        return [DEFAULT_LOCALE];
      }

      const supportedLocalesForFlow = PATH_LOCALE_OPTIONS_BY_BASE_PATH[translatedFlowPath];

      const allowedLocalesSet = new Set(allowedLocales);
      return supportedLocalesForFlow.locales.filter((locale) => allowedLocalesSet.has(locale));
    },
    [allowedLocales]
  );

  return {
    getAvailableLocales,
    allowedLocales,
    setAllowedLocales,
  };
}

export const [LocaleConfigProvider, useLocaleConfigContext] = constate(useLocaleConfig);
